<template>
    <b-container fluid>
        <b-row class="no-gutters p-2">
            <b-col md="12">
                <grid
                    :isAutoWidth="true"
                    :isSearch="false"
                    ref="grid"
                    :rowData="rowData"
                    :columnDefs="feeColumnDefs"
                    class="metadata-grid mb-2"
                    :isAutoHeight="true"
                    rowSelection="single"
                    @selection-changed="handleRowSelect"
                    @on-row-data-updated="initialRowSelect"
                >
                    <responsive-button @click="openAddFeeModal" variant="primary" :icon="['fas', 'plus']"
                        >Add Fee</responsive-button
                    >
                </grid>

                <grid
                    v-if="selectedRow && rowData.length"
                    :isSearch="false"
                    ref="property_grid"
                    :rowData="properties"
                    :columnDefs="propertiesColumnDefs"
                    :isAutoHeight="true"
                    class="pt-2"
                >
                    <responsive-dropdown class="filter-dropdown" :items="propertiesFilterOptions" variant="default">
                        <span v-html="filterIcon" />{{ filterTitle }}
                    </responsive-dropdown>
                    <responsive-button @click="bulkAssignToProperty(true)" variant="default" :icon="['fas', 'plus']"
                        >Add "{{ truncatedTitle }}" to All Properties</responsive-button
                    >
                    <responsive-button @click="bulkAssignToProperty(false)" variant="default" :icon="['fas', 'minus']"
                        >Remove "{{ truncatedTitle }}" from All Properties</responsive-button
                    >
                    <export-to-excel-button :href="exportToExcelHref"> </export-to-excel-button>
                </grid>
            </b-col>
        </b-row>
        <add-edit-fee-modal
            @hide="initialValues = null"
            :busy="busy"
            :modalId="modalId"
            :initialData="initialValues"
            @submit="onAddEditRecord"
        />
    </b-container>
</template>

<script>
    import api from '../../api';
    import { formatResponseError } from '../../utils/api';
    import Grid from '../generic/grid/grid.vue';
    import { PROPERTY_MANAGER_FEE_ENDPOINT } from '../../endpoints';
    import responsiveButton from '../generic/responsive-button/responsive-button.vue';
    import responsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
    import EditDeleteActionCellRenderer from './cell-renderers/edit-delete-action-cell-renderer.vue';
    import addEditTaxModal from './modals/add-edit-tax-modal.vue';
    import propertyManagerMixin from './property-manager-mixin';
    import addEditFeeModal from './modals/add-edit-fee-modal.vue';
    import exportToExcelButton from '../generic/export-to-excel-button/export-to-excel-button.vue';

    export default {
        mixins: [propertyManagerMixin],
        components: {
            Grid,
            responsiveButton,
            responsiveDropdown,
            EditDeleteActionCellRenderer,
            addEditTaxModal,
            addEditFeeModal,
            exportToExcelButton,
        },
        data() {
            return {
                endpoint: PROPERTY_MANAGER_FEE_ENDPOINT,
                propertyName: 'Fee',
            };
        },
        computed: {
            exportToExcelHref() {
                return `${this.endpoint()}excel/`;
            },
            feeColumnDefs() {
                return [
                    {
                        field: 'title',
                        headerName: 'Name',
                        sort: 'asc',
                    },
                    {
                        field: 'group.name',
                        headerName: 'Group',
                    },
                    {
                        field: 'type.name',
                        headerName: 'Type',
                    },
                    {
                        field: 'taxable_text',
                        headerName: 'Taxable',
                        cellRenderer: ({ data }) => data.taxable_text,
                        autoHeight: true,
                    },
                    {
                        field: 'frequency',
                        headerName: 'Frequency',
                        minWidth: 120,
                        cellRenderer: ({ data }) => {
                            if (data.is_flat) {
                                if (data.frequency == 'ONETIME') {
                                    return 'One Time';
                                }
                                return 'Daily';
                            }
                            return 'N/A';
                        },
                    },
                    {
                        field: 'collection_rules',
                        headerName: 'Collection Rules',
                        cellRenderer: ({ data }) => {
                            if (data.type.sysname != 'STAY_COLLECTED') {
                                return data.collection_rules
                            }
                            else {
                                return 'During Stay'
                            }
                            
                            return 'Not Applicable';
                        },                        
                    },
                    {
                        field: '',
                        headerName: 'Owner Statement	',
                        cellRenderer: ({ data }) => {
                            if (data.commissionable == 1) {
                                return 'Include as revenue (commissioned)';
                            } else if (data.commissionable == 2) {
                                return 'Include as revenue (no commission)';
                            }
                            return 'Not Applicable';
                        },
                    },
                    {
                        field: 'display_value',
                        headerName: 'Value',
                        autoWidthExclude: true,
                        wrapText: true,
                        autoHeight: true,
                    },
                    {
                        field: '',
                        cellRenderer: 'EditDeleteActionCellRenderer',
                        maxWidth: 210,
                        minWidth: 210,
                        cellRendererParams: {
                            onEdit: (data) => {
                                this.openEditFeeModal(data);
                            },
                            onDelete: async (data) => {
                                try {
                                    await api.delete(this.endpoint(data.id));
                                    this.fetchRowData().then(() => {
                                        this.initialRowSelect();
                                    });
                                } catch (e) {
                                    const { generic } = formatResponseError(e);
                                    this.errorToast({ text: generic || 'Could not delete fee' });
                                }
                            },
                            deleteMessage:
                                'Are you sure you want to delete this fee and remove it from all properties?',
                        },
                    },
                ];
            },
        },
        mounted() {
            this.fetchRowData()
        },
        methods: {
            openAddFeeModal() {
                this.modalId = 'add-tax-modal';
                this.$nextTick(() => {
                    this.$bvModal.show(this.modalId);
                });
            },
            openEditFeeModal(data) {
                this.modalId = 'edit-tax-modal-' + data.id;
                this.initialValues = data;
                this.$nextTick(() => {
                    this.$bvModal.show(this.modalId);
                });
            },
        },
    };
</script>

<style scoped lang="scss">
    :deep(.metadata-grid) {
        display: inline;

        .ag-row-hover,
        .ag-row-selected {
            background: rgba(33, 150, 243, 0.1);
            cursor: pointer;
        }
    }

    .select-wrapper {
        display: flex;
        align-items: baseline;
        gap: 5px;

        span {
            width: 250px;
        }
    }
</style>
<style lang="scss">
    .filter-dropdown {
        a.dropdown-item {
            display: flex;
        }
    }
</style>
